import { Component, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AuthService } from '../../../core/services/auth.service';
import { TenantContextService } from '../../../core/services/tenant-context.service';
import { TenantsStore } from '../../../core/stores/tenants.store';
import { BusinessUnitStore } from '../../../core/stores/business-unit.store';
import { ContextSelectorModalService } from '../../../core/services/context-selector-modal.service';

/**
 * Top-bar affordance that displays the currently selected tenant + Business Unit
 * and opens the context-selector modal on click. Role-aware: SuperAdmin sees a
 * two-line "Village · Committee" pill; tenant-scoped users see just the BU.
 */
@Component({
  selector: 'app-context-pill',
  standalone: true,
  imports: [CommonModule],
  template: `
    @if (visible()) {
      <button
        type="button"
        (click)="open()"
        [title]="tooltip()"
        [attr.aria-label]="tooltip()"
        class="group flex items-center gap-2 sm:gap-2.5 pl-2.5 pr-2 py-1.5 min-w-0 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 hover:border-gray-300 transition focus:outline-none focus:ring-2 focus:ring-blue-500/30"
      >
        <div class="w-7 h-7 rounded-md bg-blue-50 flex items-center justify-center flex-shrink-0">
          <svg class="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"/>
          </svg>
        </div>
        <div class="flex flex-col items-start min-w-0 leading-tight text-left">
          @if (showTenantLine()) {
            <span class="text-[10px] font-medium text-gray-500 uppercase tracking-wider truncate max-w-[34vw] sm:max-w-[140px]">
              {{ tenantName() }}
            </span>
          }
          <span class="text-sm font-semibold text-gray-900 truncate max-w-[34vw] sm:max-w-[160px]">
            @if (buName()) { {{ buName() }} }
            @else { Select committee }
          </span>
        </div>
        <svg class="w-4 h-4 text-gray-400 group-hover:text-gray-600 flex-shrink-0 transition" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"/>
        </svg>
      </button>
    }
  `,
  styles: [`:host { display: inline-flex; min-width: 0; }`],
})
export class ContextPillComponent {
  private auth     = inject(AuthService);
  private ctx      = inject(TenantContextService);
  private tenants  = inject(TenantsStore);
  private buStore  = inject(BusinessUnitStore);
  private modalSvc = inject(ContextSelectorModalService);

  readonly isSuper = computed(() => this.auth.isSuperAdminSignal());

  readonly tenantName = computed(() => {
    const id = this.ctx.selectedTenantId();
    if (!id) return '';
    return this.tenants.byId().get(id)?.villageName || this.auth.tenant()?.tenantName || '';
  });

  readonly buName = computed(() => {
    const id = this.ctx.selectedBusinessUnitId();
    if (!id) return '';
    return this.buStore.byId().get(id)?.name || this.auth.tenant()?.businessUnitName || '';
  });

  /** Show the small tenant label on top for SuperAdmin only. Tenant-scoped users
   * always work within one tenant, so the BU name is the only relevant context. */
  readonly showTenantLine = computed(() => this.isSuper() && !!this.tenantName());

  readonly visible = computed(() => {
    return this.auth.isLoggedIn() && (!!this.tenantName() || !!this.buName());
  });

  readonly tooltip = computed(() => {
    const t = this.tenantName();
    const b = this.buName();
    if (t && b) return `${t} · ${b} — click to switch`;
    if (b) return `${b} — click to switch`;
    if (t) return `${t} — click to switch`;
    return 'Switch workspace';
  });

  open(): void {
    this.modalSvc.open();
  }
}
